import { useQueryClient } from '@tanstack/react-query';

export const queryKeys = {
  auth: {
    session: () => ['auth', 'session'] as const,
    me: () => ['auth', 'me'] as const,
    tenants: () => ['auth', 'tenants'] as const,
  },
  tenant: {
    current: (tenantId: string) => ['tenant', tenantId] as const,
    config: (tenantId: string) => ['tenant', tenantId, 'config'] as const,
  },
  dashboard: {
    summary: (tenantId: string) => ['tenant', tenantId, 'dashboard', 'summary'] as const,
    cashPosition: (tenantId: string) => ['tenant', tenantId, 'dashboard', 'cash-position'] as const,
    salesSummary: (tenantId: string) => ['tenant', tenantId, 'dashboard', 'sales-summary'] as const,
    stockAlerts: (tenantId: string) => ['tenant', tenantId, 'dashboard', 'stock-alerts'] as const,
  },
  sales: {
    list: (tenantId: string) => ['tenant', tenantId, 'sales', 'list'] as const,
    detail: (tenantId: string, saleId: string) => ['tenant', tenantId, 'sales', saleId] as const,
    quotes: () => ['sales', 'quotes'] as const,
  },
  pos: {
    session: (tenantId: string, terminalId: string) =>
      ['tenant', tenantId, 'pos', 'session', terminalId] as const,
    products: (tenantId: string) => ['tenant', tenantId, 'pos', 'products'] as const,
    sessions: (tenantId: string) => ['tenant', tenantId, 'pos', 'sessions'] as const,
    terminals: () => ['pos', 'terminals'] as const,
  },
  inventory: {
    stock: (tenantId: string) => ['tenant', tenantId, 'inventory', 'stock'] as const,
    stockItem: (tenantId: string, productId: string) =>
      ['tenant', tenantId, 'inventory', 'stock', productId] as const,
    warehouses: () => ['inventory', 'warehouses'] as const,
    movements: () => ['inventory', 'movements'] as const,
    batches: () => ['inventory', 'batches'] as const,
    serials: () => ['inventory', 'serials'] as const,
    counts: () => ['inventory', 'counts'] as const,
    reservations: () => ['inventory', 'reservations'] as const,
  },
  finance: {
    dashboard: (tenantId: string) => ['tenant', tenantId, 'finance', 'dashboard'] as const,
    receivables: (tenantId?: string) => tenantId ? ['tenant', tenantId, 'finance', 'receivables'] as const : ['finance', 'receivables'] as const,
    payables: (tenantId?: string) => tenantId ? ['tenant', tenantId, 'finance', 'payables'] as const : ['finance', 'payables'] as const,
    accounts: () => ['finance', 'accounts'] as const,
    reconciliation: () => ['finance', 'reconciliation'] as const,
  },
  manufacturing: {
    productionOrders: (tenantId: string) =>
      ['tenant', tenantId, 'manufacturing', 'production-orders'] as const,
    productionOrder: (tenantId: string, orderId: string) =>
      ['tenant', tenantId, 'manufacturing', 'production-orders', orderId] as const,
    boms: () => ['manufacturing', 'boms'] as const,
  },
  fiscal: {
    list: (tenantId: string) => ['tenant', tenantId, 'fiscal', 'invoices'] as const,
    detail: (tenantId: string, id: string) => ['tenant', tenantId, 'fiscal', 'invoices', id] as const,
    taxRates: () => ['fiscal', 'tax-rates'] as const,
    taxRules: () => ['fiscal', 'tax-rules'] as const,
    taxProfiles: () => ['fiscal', 'tax-profiles'] as const,
  },
  iam: {
    sessions: () => ['iam', 'sessions'] as const,
    memberships: () => ['iam', 'memberships'] as const,
    accessRequests: () => ['iam', 'access-requests'] as const,
  },
  masterData: {
    parties: (tenantId: string) => ['tenant', tenantId, 'master-data', 'parties'] as const,
    products: (tenantId: string) => ['tenant', tenantId, 'master-data', 'products'] as const,
    services: () => ['master-data', 'services'] as const,
    categories: () => ['master-data', 'categories'] as const,
    customers: () => ['master-data', 'customers'] as const,
    importJobs: () => ['master-data', 'import'] as const,
    importJob: (id: string) => ['master-data', 'import', id] as const,
  },
  procurement: {
    requests: (tenantId: string) => ['tenant', tenantId, 'procurement', 'requests'] as const,
    orders: () => ['procurement', 'orders'] as const,
    receipts: () => ['procurement', 'receipts'] as const,
    rfqs: () => ['procurement', 'rfqs'] as const,
    rfq: (id: string) => ['procurement', 'rfqs', id] as const,
  },
  support: {
    tickets: (tenantId: string) => ['tenant', tenantId, 'support', 'tickets'] as const,
  },
  integration: {
    webhooks: () => ['integration', 'webhooks'] as const,
    exports: () => ['integration', 'exports'] as const,
    imports: () => ['integration', 'imports'] as const,
  },
  accounting: {
    chart: () => ['accounting', 'chart'] as const,
    entries: () => ['accounting', 'entries'] as const,
    trialBalance: () => ['accounting', 'trial-balance'] as const,
    incomeStatement: () => ['accounting', 'income-statement'] as const,
    balanceSheet: () => ['accounting', 'balance-sheet'] as const,
  },
  organization: {
    companies: () => ['organization', 'companies'] as const,
    branches: () => ['organization', 'branches'] as const,
    numberSequences: () => ['organization', 'number-sequences'] as const,
  },
  audit: {
    logs: (params?: Record<string, unknown>) => ['audit', 'logs', params] as const,
    logDetail: (id: string) => ['audit', 'logs', 'detail', id] as const,
  },
  observability: {
    errorLogs: (params?: Record<string, unknown>) => ['admin', 'error-logs', params] as const,
  },
};

type QueryKeyFn = (...args: any[]) => readonly unknown[];

export function useInvalidateQueries(): (keys: QueryKeyFn) => void {
  const queryClient = useQueryClient();

  return (keys) => {
    queryClient.invalidateQueries({ queryKey: keys as any }).catch((error) => {
      console.error('Failed to invalidate queries:', error);
    });
  };
}
